import React, { FC, ComponentProps, useState, useMemo } from 'react';
import { Autocomplete } from '@workboard/wobo-ui';
import { useTranslation } from 'react-i18next';

import { AiWorkstreamData } from '../ActionItemPopup/types';
import { Option, useAutocomplete } from '../utils/useAutocomplete';
import { useWorkstreamInfo } from './useWorkstreamInfo';

type AutocompleteProps = ComponentProps<typeof Autocomplete>;

export interface IncomingWorkstream {
  id: string;
  name: string;
  teamName: string;
  label?: string;
}
interface IncomingData {
  ws: IncomingWorkstream[];
}

export function useWorkstreamAutocomplete(
  value?: string,
  initialData?: Partial<Option>
) {
  return useAutocomplete<IncomingWorkstream, IncomingData>({
    url: `search/workstream`,
    value,
    initialData,
    getDataArray: (data) => data?.ws ?? [],
    getOption: (ws) => ({
      id: ws.id,
      label: ws.name,
      teamName: ws?.teamName,
    }),
  });
}

interface IProps extends AutocompleteProps {
  aiId: string;
  value: string;
  initialData?: Partial<IncomingWorkstream>;
  filter?: (item: AiWorkstreamData) => boolean;
}

export const WorkstreamAutocompleteField: FC<IProps> = ({
  aiId,
  value,
  filter,
  initialData,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    options: workstreams,
    loading,
    handleSearch,
  } = useWorkstreamInfo({ aiId });
  const options = useMemo(
    () => (filter ? workstreams.filter(filter) : workstreams),
    [workstreams, filter]
  );
  const valObj = options?.find((ws) => ws?.id === value) || null;
  const [inputValue, setInputValue] = useState(initialData?.label || '');
  return (
    <Autocomplete
      label={t('Workstream')}
      {...props}
      loading={loading}
      onInputChange={(e, text) => {
        if (e) {
          setInputValue(text);
          handleSearch(text);
        }
      }}
      value={valObj}
      options={options || []}
      startMatch={false}
      inputValue={inputValue}
      groupBy={(o) => o?.tname}
      getOptionSelected={(ws) => ws?.id === value}
    />
  );
};
